import { useLocation, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Search, BadgeCheck, Lock, Star, Heart, MapPin } from "lucide-react";
import { SEO_CITIES } from "@/lib/seoCities";

const SITE = "https://clickandgard.fr";

interface CategoryConfig {
  slug: string;
  h1: string;
  title: string;
  description: string;
  intro: string;
  body: string[];
  faq: Array<{ q: string; a: string }>;
}

const CATEGORIES: Record<string, CategoryConfig> = {
  "baby-sitter": {
    slug: "baby-sitter",
    h1: "Baby-sitter en France — réservation en ligne",
    title: "Baby-sitter en France — Trouvez une nounou vérifiée",
    description: "Trouvez une baby-sitter de confiance partout en France sur Click&Gard. Profils vérifiés, avis parents, réservation en ligne et 0% commission.",
    intro: "Click&Gard est la plateforme française pour trouver une baby-sitter vérifiée près de chez vous. Réservez votre nounou en quelques clics, sans frais cachés.",
    body: [
      "Une <strong>baby-sitter</strong> est un professionnel ou une professionnelle qui assure la garde ponctuelle ou régulière d'enfants au domicile des parents ou à son propre domicile. Sur Click&Gard, chaque baby-sitter inscrite est vérifiée (identité, diplômes) avant de pouvoir recevoir des demandes de garde.",
      "Vous pouvez réserver une <strong>baby-sitter à l'heure</strong>, à la journée, en soirée ou pour le week-end. Les tarifs varient généralement entre 10 € et 15 € de l'heure selon la ville, l'expérience et les services proposés (sortie d'école, repas, devoirs, bain, garde de fratrie).",
      "Click&Gard met à votre disposition une messagerie sécurisée pour échanger avec la baby-sitter avant de confirmer la garde. Le règlement de la prestation se fait ensuite directement entre vous et la nounou (espèces, CESU, virement, PayPal…), sans intermédiaire ni commission.",
    ],
    faq: [
      { q: "Quel est le tarif d'une baby-sitter ?", a: "En France, le tarif horaire d'une baby-sitter se situe généralement entre 10 € et 15 €. Le prix dépend de la ville, de l'expérience, des qualifications et du nombre d'enfants à garder. Sur Click&Gard, le tarif est affiché avant la réservation." },
      { q: "Comment trouver une baby-sitter de confiance ?", a: "Privilégiez les plateformes qui vérifient l'identité et les diplômes des professionnels. Sur Click&Gard, chaque baby-sitter passe une vérification d'identité et une validation manuelle par notre équipe, et les avis sont laissés uniquement par des parents ayant réellement réservé." },
      { q: "Peut-on réserver une baby-sitter en urgence ?", a: "Oui. Click&Gard propose un filtre « Disponible immédiatement » qui affiche les baby-sitters prêtes à intervenir le jour même. Vous pouvez les contacter et obtenir une réponse en quelques minutes." },
    ],
  },
  "nounou": {
    slug: "nounou",
    h1: "Nounou à domicile — Trouver une nounou de confiance",
    title: "Nounou à domicile — Garde d'enfants vérifiée",
    description: "Trouvez une nounou à domicile vérifiée sur Click&Gard. Garde régulière, périscolaire ou ponctuelle, partout en France. Inscription gratuite pour les parents.",
    intro: "Une nounou à domicile prend en charge vos enfants chez vous. Click&Gard vous met en relation avec des nounous vérifiées partout en France.",
    body: [
      "La <strong>nounou à domicile</strong> est la solution idéale pour les parents qui souhaitent que leurs enfants restent dans leur environnement habituel. Elle peut intervenir pour de la <strong>garde périscolaire</strong> (sortie d'école, devoirs, bain, repas) ou pour des gardes ponctuelles en soirée et week-end.",
      "Sur Click&Gard, vous trouvez des nounous expérimentées avec différents profils : étudiantes, professionnels du secteur, retraités du milieu de l'enfance. Chaque profil indique l'expérience, les diplômes (CAP Petite Enfance, BAFA, formation premiers secours), les services proposés et le tarif horaire.",
      "La <strong>garde d'enfants à domicile</strong> peut être ouverte droit au crédit d'impôt selon les règles fiscales en vigueur. Click&Gard fournit les informations nécessaires pour vous accompagner dans vos démarches.",
    ],
    faq: [
      { q: "Quelle différence entre nounou et baby-sitter ?", a: "Les termes sont souvent interchangeables. La nounou désigne plutôt une garde régulière (souvent à domicile) tandis que la baby-sitter renvoie plus à des gardes ponctuelles. Sur Click&Gard, vous trouvez les deux profils." },
      { q: "Comment fonctionne la garde périscolaire ?", a: "La nounou récupère votre enfant à la sortie de l'école, l'accompagne à la maison ou à une activité, gère le goûter, les devoirs et le bain jusqu'à votre retour. Filtrez les profils proposant ces services sur Click&Gard." },
      { q: "Peut-on bénéficier du crédit d'impôt ?", a: "La garde d'enfants à domicile peut ouvrir droit à un crédit d'impôt selon les règles fiscales en vigueur. Renseignez-vous auprès de l'URSSAF ou de votre centre des impôts." },
    ],
  },
  "garde-enfants": {
    slug: "garde-enfants",
    h1: "Garde d'enfants — La solution Click&Gard",
    title: "Garde d'enfants — Réservation en ligne sécurisée",
    description: "Garde d'enfants en France : trouvez une nounou ou baby-sitter vérifiée sur Click&Gard. Réservation en ligne, 0% commission, partout en France.",
    intro: "La garde d'enfants demande confiance et flexibilité. Click&Gard vous met en relation avec des professionnels vérifiés en quelques clics.",
    body: [
      "La <strong>garde d'enfants</strong> regroupe l'ensemble des solutions permettant de confier ses enfants à un tiers de confiance : <strong>baby-sitter</strong>, <strong>nounou à domicile</strong>, <strong>assistante maternelle</strong>. Chaque solution a ses spécificités selon vos besoins : garde régulière ou ponctuelle, à votre domicile ou chez la professionnelle.",
      "Click&Gard simplifie la recherche en regroupant tous les types de garde sur une seule plateforme. Indiquez votre ville, le créneau souhaité et les services attendus, et accédez à une sélection de profils vérifiés correspondant à votre demande.",
      "Notre processus de vérification en trois étapes (email, identité, validation manuelle) garantit la fiabilité des professionnels. Click&Gard ne gère pas les paiements de garde : le règlement se fait directement entre le parent et la nounou, sans commission. Notre service de médiation reste disponible en cas de litige pour protéger les deux parties.",
    ],
    faq: [
      { q: "Quels sont les modes de garde d'enfants en France ?", a: "Les principaux modes sont la baby-sitter, la nounou à domicile, l'assistante maternelle agréée, la crèche collective ou parentale, et la garde partagée entre familles. Click&Gard couvre les gardes individuelles à domicile." },
      { q: "Quel coût prévoir pour la garde d'enfants ?", a: "À domicile, comptez entre 10 € et 15 € de l'heure. Les prix varient selon la ville, l'expérience du professionnel et les services demandés. Click&Gard est gratuit pour les parents : vous payez uniquement la prestation." },
      { q: "La garde d'enfants Click&Gard est-elle déclarée ?", a: "Oui, vous pouvez déclarer votre garde via le CESU ou Pajemploi selon votre situation. Click&Gard fournit les éléments nécessaires (heures effectuées, montants) à la fin de chaque prestation." },
    ],
  },
  "assistante-maternelle": {
    slug: "assistante-maternelle",
    h1: "Assistante maternelle — Trouver une professionnelle agréée",
    title: "Assistante maternelle — Garde agréée en France",
    description: "Trouvez une assistante maternelle ou une nounou vérifiée sur Click&Gard. Garde régulière, sortie d'école, profils contrôlés et avis parents.",
    intro: "Click&Gard regroupe assistantes maternelles agréées et nounous vérifiées partout en France. Trouvez la solution adaptée à votre famille.",
    body: [
      "Une <strong>assistante maternelle</strong> est une professionnelle agréée par les services départementaux pour accueillir des enfants à son domicile. C'est une solution privilégiée par les familles cherchant une garde régulière, souvent en journée pour les enfants en bas âge.",
      "Sur Click&Gard, vous pouvez également trouver des <strong>nounous à domicile</strong> et baby-sitters pour compléter ou remplacer une assistante maternelle, par exemple en cas de garde périscolaire en sortie de crèche ou d'école, ou pour des gardes en soirée et week-end.",
      "Chaque professionnel inscrit indique ses qualifications (agrément, CAP Petite Enfance, formations), ses disponibilités et son tarif. La messagerie sécurisée vous permet d'échanger avant la réservation, et le règlement se fait directement avec la professionnelle, sans commission de Click&Gard.",
    ],
    faq: [
      { q: "Quelle différence entre assistante maternelle et nounou ?", a: "L'assistante maternelle est agréée par le département et garde les enfants à son domicile. La nounou intervient au domicile des parents et ne nécessite pas d'agrément. Click&Gard vous aide à trouver les deux selon vos besoins." },
      { q: "Comment vérifier l'agrément d'une assistante maternelle ?", a: "L'agrément est délivré par le service de PMI du département. Sur Click&Gard, les assistantes maternelles peuvent indiquer leur numéro d'agrément dans leur profil, en plus de la vérification d'identité Click&Gard." },
      { q: "Combien coûte une assistante maternelle ?", a: "Le tarif horaire d'une assistante maternelle agréée varie selon la convention collective et la zone géographique, avec un minimum légal. Pour une nounou à domicile, comptez 10 € à 15 € de l'heure." },
    ],
  },
};

const CategoryHub = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, "").split("/")[0];
  const config = slug ? CATEGORIES[slug] : undefined;

  if (!config) return <Navigate to="/babysitter-france" replace />;

  const path = `/${config.slug}`;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: config.h1, item: `${SITE}${path}` },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: config.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: config.h1,
    description: config.description,
    areaServed: { "@type": "Country", name: "France" },
    provider: { "@type": "Organization", name: "Click&Gard", url: SITE },
    serviceType: "Garde d'enfants",
  };

  return (
    <div className="min-h-screen bg-immersive">
      <Seo title={config.title} description={config.description} path={path} jsonLd={[breadcrumbJsonLd, faqJsonLd, serviceJsonLd]} />
      <Navbar />

      <section className="relative overflow-hidden py-16 md:py-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="relative max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            {config.h1}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">{config.intro}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button size="lg" variant="neon" className="text-base font-semibold">
                <Search className="w-5 h-5" />
                Trouver un professionnel
              </Button>
            </Link>
            <Link to="/auth?tab=register">
              <Button size="lg" variant="outline" className="text-base font-semibold">Créer un compte gratuit</Button>
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-primary" /> Profils vérifiés</span>
            <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-primary" /> 0% commission</span>
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-primary" /> Avis vérifiés</span>
            <span className="flex items-center gap-2"><Heart className="w-4 h-4 text-primary" /> Gratuit pour les parents</span>
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto prose prose-invert prose-lg">
          <article className="space-y-6 text-foreground/90">
            {config.body.map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
            ))}
          </article>
        </div>
      </section>

      <section className="py-12 px-4 bg-card/30 border-y border-border/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-center mb-10">Questions fréquentes</h2>
          <div className="space-y-4">
            {config.faq.map((f, i) => (
              <article key={i} className="rounded-xl border border-border/50 bg-card/50 p-6">
                <h3 className="font-semibold text-lg mb-2 text-foreground">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">Disponible dans toute la France</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {SEO_CITIES.slice(0, 18).map((c) => (
              <Link key={c.slug} to={`/baby-sitter/${c.slug}`} className="px-4 py-2 rounded-full bg-card/50 border border-border/50 text-sm hover:border-primary/40 hover:text-primary transition-all flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {c.name}
              </Link>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Vous cherchez un ordre de prix ?{" "}
            <Link to="/tarifs-baby-sitting" className="text-primary underline">
              Consultez les tarifs d'une baby-sitter par ville
            </Link>
            .
          </p>

        </div>
      </section>
    </div>
  );
};

export default CategoryHub;
